import { BlockStack, Button, Checkbox, ChoiceList, InlineError, InlineStack, Label, Tabs } from '@shopify/polaris';
import { PlusCircleIcon } from '@shopify/polaris-icons';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import BoldText from '@/components/BoldText';
import { config } from '@/config';
import { DateChoicesEnum, KindOfTimeType, WorkingDayType } from '@/constants/enum';
import {
  deliverySettingSelector,
  deliverySpecificWorkingDaySelector,
  deliveryStateSelector,
  deliveryWeeklyWorkingDaySelector,
  handleSaveDeliverySettings,
  handleSaveDeliveryWeeklyWorkingDays,
  handleSaveDeliveryWeeklyWorkingDaysLastState,
  handleSetDeliveryState,
} from '@/redux/slice/delivery.slice';
import { errorFieldsSelector, handleSetErrorFields } from '@/redux/slice/error.slice';
import { ITimeSlot } from '@/types';
import { useDispatch, useSelector } from 'react-redux';
import CopySetup from './CopySetup';
import DeliveryExactTime from './DeliveryExactTime';
import SpecificDate from './SpecificDate';
import TimeSlot from './Timeslot';

interface DeliveryTimeProps {
  time24h: { id: number; value: string }[];
}

const DeliveryTime: FC<DeliveryTimeProps> = ({ time24h }) => {
  const { t } = useTranslation('deliverySettings');
  const deliverySettings = useSelector(deliverySettingSelector);
  const deliveryState = useSelector(deliveryStateSelector);
  const deliveryWeeklyWorkingDays = useSelector(deliveryWeeklyWorkingDaySelector);
  const deliveryWeeklyWorkingDaysLastState = useSelector(deliveryWeeklyWorkingDaySelector);
  const deliverySpecificDays = useSelector(deliverySpecificWorkingDaySelector);
  const errorFields = useSelector(errorFieldsSelector);
  const dispatch = useDispatch();

  const [selectTabDays, setSelectTabDays] = useState<number>(0);

  const handleSelectDeliveryTime = (value: string[]) => {
    dispatch(handleSaveDeliverySettings({ deliveryTimeSlotType: value[0] as KindOfTimeType }));
  };

  const handleTabDayChange = useCallback(
    (value: number) => {
      dispatch(handleSetDeliveryState({ isSaving: false }));
      setSelectTabDays(value);
    },
    [dispatch],
  );

  const deliveryDayLabels = useMemo(() => {
    return [
      t('sections.deliveryAvailability.deliveryTime.deliveryDayTabs.0'), // sunday
      t('sections.deliveryAvailability.deliveryTime.deliveryDayTabs.1'), // monday
      t('sections.deliveryAvailability.deliveryTime.deliveryDayTabs.2'), // tuesday
      t('sections.deliveryAvailability.deliveryTime.deliveryDayTabs.3'), // wednesday
      t('sections.deliveryAvailability.deliveryTime.deliveryDayTabs.4'), // thursday
      t('sections.deliveryAvailability.deliveryTime.deliveryDayTabs.5'), // friday
      t('sections.deliveryAvailability.deliveryTime.deliveryDayTabs.6'), // saturday
    ];
  }, [t]);

  const workingDayChecks = useMemo(() => {
    return deliveryWeeklyWorkingDays
      .filter((item) => item.day >= 0)
      .sort((a, b) => {
        const dayA = a.day === 0 ? 7 : a.day;
        const dayB = b.day === 0 ? 7 : b.day;
        return dayA - dayB;
      });
  }, [deliveryWeeklyWorkingDays]);

  const workingDayTabs = useMemo(() => {
    return workingDayChecks
      .filter((item) => item.isEnable)
      .map((item) => ({
        id: item.day.toString(),
        content: deliveryDayLabels[item.day],
        panelID: item.day.toString(),
      }));
  }, [deliveryDayLabels, workingDayChecks]);

  const selectedWorkingDay = useMemo(() => {
    if (workingDayTabs.length > 0 && deliverySettings.deliveryTimeSlotType === KindOfTimeType.DIFFERENT) {
      return deliveryWeeklyWorkingDays.find((item) => item.day === Number(workingDayTabs[selectTabDays]?.id));
    }
    return deliveryWeeklyWorkingDays.find((item) => item.day === config.SAME_DAY_VALUE);
  }, [deliverySettings.deliveryTimeSlotType, deliveryWeeklyWorkingDays, selectTabDays, workingDayTabs]);

  const handleCheckValidateTimeSlots = (
    timeSlots: ITimeSlot[],
    check_fill: boolean,
    check_Dup: number,
    item_non_timeSlots: number[],
    index: number,
  ) => {
    if (Array.isArray(timeSlots) && timeSlots.length) {
      timeSlots.forEach((item, i) => {
        if (!item.start || !item.end) {
          check_fill = true;
          item_non_timeSlots.push(index);
        }
        timeSlots.forEach((slot, i_check) => {
          if (i !== i_check) {
            if (item.start.toString() === slot.start.toString() && item.end.toString() === slot.end.toString()) {
              check_Dup = i;
            }
          }
        });
      });
      dispatch(
        handleSetErrorFields({
          errorFields: {
            rule_addTimeSlot:
              item_non_timeSlots.length > 0 && deliverySettings.deliveryChoice.includes(DateChoicesEnum.DATETIMESLOT),
            rule_duplicateTimeSLot: check_Dup !== -1 && deliverySettings.deliveryChoice.includes(DateChoicesEnum.DATETIMESLOT),
            requireTimeSlot: timeSlots.length === 0 && deliverySettings.deliveryChoice.includes(DateChoicesEnum.DATETIMESLOT),
          },
        }),
      );
    }
  };

  useEffect(() => {
    let check_fill = false;
    let check = -1;
    let item_non_timeSlots: number[] = [];
    if (deliverySettings.deliveryTimeSlotType === KindOfTimeType.DIFFERENT) {
      const weeklyWorkingDays = [...deliveryWeeklyWorkingDays];
      weeklyWorkingDays
        .sort((a, b) => {
          const dayA = a.day === 0 ? 7 : a.day;
          const dayB = b.day === 0 ? 7 : b.day;
          return dayA - dayB;
        })
        .filter((item) => item.isEnable)
        .forEach((currentTimeSlot) => {
          handleCheckValidateTimeSlots(
            currentTimeSlot?.timeSlots as ITimeSlot[],
            check_fill,
            check,
            item_non_timeSlots,
            currentTimeSlot.id,
          );
          if (deliveryState.isSaving && item_non_timeSlots.length > 0) {
            dispatch(handleSetDeliveryState({ isCheckingTimeSlot: true }));
            workingDayChecks
              .filter((item) => item.isEnable)
              .forEach((item, index) => {
                if (
                  selectedWorkingDay &&
                  item_non_timeSlots.includes(Number(selectedWorkingDay?.id)) &&
                  item.id === selectedWorkingDay?.id
                ) {
                  setSelectTabDays(index);
                } else if (item_non_timeSlots.includes(item.id)) {
                  setSelectTabDays(index);
                }
              });
          }
        });
    } else {
      handleCheckValidateTimeSlots(selectedWorkingDay?.timeSlots as ITimeSlot[], check_fill, check, item_non_timeSlots, -1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    deliverySettings.deliveryChoice,
    deliverySettings.deliveryTimeSlotType,
    deliveryState.isSaving,
    deliveryWeeklyWorkingDays,
    dispatch,
    selectedWorkingDay,
    selectedWorkingDay?.timeSlots,
    workingDayChecks,
  ]);

  useEffect(() => {
    if (deliverySettings.deliveryChoice === DateChoicesEnum.DATETIME) {
      let check_Fill = -1;
      let item_non_timeSlots: number[] = [];

      if (deliverySettings.deliveryTimeSlotType === KindOfTimeType.DIFFERENT) {
        workingDayChecks
          .filter((item) => item.isEnable)
          .forEach((currentTimeSlot, index) => {
            if (Object.keys(currentTimeSlot?.exactTimes ?? {}).length === 0) {
              check_Fill += 1;
              item_non_timeSlots.push(index);
            }
          });

        if (deliveryState.isSaving && item_non_timeSlots.length > 0) {
          handleTabDayChange(item_non_timeSlots[0]);
        }

        dispatch(
          handleSetErrorFields({
            errorFields: {
              rule_addTimeSlot_exact: check_Fill !== -1,
            },
          }),
        );
      } else {
        dispatch(
          handleSetErrorFields({
            errorFields: {
              rule_addTimeSlot_exact: selectedWorkingDay?.exactTimes?.length === 0,
            },
          }),
        );
      }
    }
  }, [
    dispatch,
    handleTabDayChange,
    deliverySettings.deliveryChoice,
    deliverySettings.deliveryTimeSlotType,
    deliveryState.isSaving,
    selectedWorkingDay?.exactTimes,
    workingDayChecks,
  ]);

  useEffect(() => {
    dispatch(
      handleSetErrorFields({
        errorFields: {
          errorSpecificDays: deliverySpecificDays.length === 0 && deliverySettings.workingDayType === WorkingDayType.CUSTOM,
        },
      }),
    );
  }, [dispatch, deliverySettings.workingDayType, deliverySpecificDays]);

  const handleChangeTimeSlot = useCallback(
    (timeSlot: ITimeSlot, index: number) => {
      dispatch(
        handleSetDeliveryState({
          isCheckingTimeSlot: false,
        }),
      );
      dispatch(
        handleSetErrorFields({
          errorFields: {
            rule_addTimeSlot: false,
            rule_duplicateTimeSLot: false,
            requireTimeSlot: false,
          },
        }),
      );

      // check time slots
      let currentTimeSlot = selectedWorkingDay?.timeSlots?.map((item) => ({ ...item })) ?? [];
      currentTimeSlot[index].start = timeSlot.start;
      currentTimeSlot[index].end = timeSlot.end;

      let check = -1;
      currentTimeSlot.forEach((item: ITimeSlot, i) => {
        currentTimeSlot.forEach((slot: ITimeSlot, i_check) => {
          if (i !== i_check) {
            if (item.start.toString() === slot.start.toString() && item.end.toString() === slot.end.toString()) {
              check = i;
            }
          }
        });
      });
      dispatch(
        handleSetErrorFields({
          errorFields: {
            rule_addTimeSlot: false,
            rule_duplicateTimeSLot: check !== -1,
            requireTimeSlot: false,
          },
        }),
      );

      dispatch(
        handleSaveDeliveryWeeklyWorkingDays(
          deliveryWeeklyWorkingDays.map((item) => {
            if (item.day === selectedWorkingDay?.day) {
              return {
                ...item,
                timeSlots: item.timeSlots?.map((item: ITimeSlot, idx: number) => {
                  if (idx === index) {
                    return timeSlot;
                  }
                  return item;
                }),
              };
            }
            return item;
          }),
        ),
      );
    },
    [deliveryWeeklyWorkingDays, dispatch, selectedWorkingDay?.day, selectedWorkingDay?.timeSlots],
  );

  const handleDeleteTimeSlot = useCallback(
    (index: number) => {
      dispatch(
        handleSetDeliveryState({
          isCheckingTimeSlot: false,
        }),
      );
      const listDelete = selectedWorkingDay?.timeSlots.filter((_, idx) => idx !== index);
      dispatch(
        handleSetErrorFields({
          errorFields: {
            rule_addTimeSlot: false,
            rule_duplicateTimeSLot: false,
            requireTimeSlot: listDelete ? listDelete.length === 0 : false,
          },
        }),
      );

      dispatch(
        handleSaveDeliveryWeeklyWorkingDays(
          deliveryWeeklyWorkingDays.map((item) => {
            if (item.day === selectedWorkingDay?.day) {
              return {
                ...item,
                timeSlots: listDelete ? listDelete : [],
              };
            }
            return item;
          }),
        ),
      );
    },
    [deliveryWeeklyWorkingDays, dispatch, selectedWorkingDay?.day, selectedWorkingDay?.timeSlots],
  );

  const handleAddTimeSlot = useCallback(() => {
    dispatch(
      handleSetDeliveryState({
        isCheckingTimeSlot: false,
      }),
    );
    let newSlots = {
      start: '',
      end: '',
      orderLimit: 0,
    };

    // check time slots
    let currentTimeSlot = selectedWorkingDay?.timeSlots?.map((item) => ({ ...item })) ?? [];
    currentTimeSlot.unshift(newSlots);
    let check_fill = false;
    let check = -1;
    currentTimeSlot.forEach((item: ITimeSlot, i) => {
      if (!item.start || !item.end) {
        check_fill = true;
      }
      currentTimeSlot.forEach((slot: ITimeSlot, i_check) => {
        if (i !== i_check) {
          if (item.start.toString() === slot.start.toString() && item.end.toString() === slot.end.toString()) {
            check = i;
          }
        }
      });
    });
    dispatch(
      handleSetErrorFields({
        errorFields: {
          rule_addTimeSlot: check_fill,
          rule_duplicateTimeSLot: check !== -1,
          requireTimeSlot: false,
        },
      }),
    );

    dispatch(
      handleSaveDeliveryWeeklyWorkingDays(
        deliveryWeeklyWorkingDays.map((item) => {
          if (item.day === selectedWorkingDay?.day) {
            return {
              ...item,
              timeSlots: [...currentTimeSlot],
            };
          }
          return item;
        }),
      ),
    );
  }, [deliveryWeeklyWorkingDays, dispatch, selectedWorkingDay?.day, selectedWorkingDay?.timeSlots]);

  const checkDisableAddTimeSlots = useMemo(() => {
    return (
      selectedWorkingDay?.timeSlots &&
      selectedWorkingDay?.timeSlots?.length > 0 &&
      (!selectedWorkingDay?.timeSlots[0]?.end || !selectedWorkingDay?.timeSlots[0]?.start)
    );
  }, [selectedWorkingDay?.timeSlots]);

  return (
    <BlockStack gap={'100'}>
      {deliverySettings.deliveryChoice !== DateChoicesEnum.SINGLEDATE && (
        <>
          <BoldText>{t('sections.deliveryAvailability.deliveryTime.title')}</BoldText>
          <ChoiceList
            title=""
            choices={[
              { label: t('sections.deliveryAvailability.deliveryTime.same'), value: KindOfTimeType.SAME },
              { label: t('sections.deliveryAvailability.deliveryTime.different'), value: KindOfTimeType.DIFFERENT },
            ]}
            selected={[deliverySettings.deliveryTimeSlotType]}
            onChange={handleSelectDeliveryTime}
          />
        </>
      )}
      {deliverySettings.workingDayType === WorkingDayType.WEEKDAYS && (
        <BlockStack gap="200">
          <Label id="deliveryDays">{t('sections.deliveryAvailability.chooseDays')}</Label>
          <InlineStack align="space-between">
            {workingDayChecks.map((day) => (
              <Checkbox
                key={day.day}
                name={`day-${day.day}`}
                label={deliveryDayLabels[day.day]}
                checked={day.isEnable}
                disabled={workingDayChecks.filter((dc) => dc.isEnable).length === 1 && day.isEnable}
                onChange={(value) => {
                  dispatch(
                    handleSaveDeliveryWeeklyWorkingDays(
                      deliveryWeeklyWorkingDays.map((item) => {
                        if (item.day === day.day) {
                          return {
                            ...item,
                            isEnable: value,
                          };
                        }
                        return item;
                      }),
                    ),
                  );
                  dispatch(
                    handleSaveDeliveryWeeklyWorkingDaysLastState(
                      deliveryWeeklyWorkingDaysLastState.map((item) => {
                        if (item.day === day.day) {
                          return {
                            ...item,
                            isEnable: value,
                          };
                        }
                        return item;
                      }),
                    ),
                  );
                }}
              />
            ))}
          </InlineStack>
        </BlockStack>
      )}
      {deliverySettings.deliveryTimeSlotType === KindOfTimeType.DIFFERENT &&
        deliverySettings.deliveryChoice !== DateChoicesEnum.SINGLEDATE &&
        workingDayTabs.length > 0 && <Tabs tabs={workingDayTabs} selected={selectTabDays} onSelect={handleTabDayChange} fitted />}
      {deliverySettings.workingDayType === WorkingDayType.CUSTOM && <SpecificDate />}
      {deliverySettings.deliveryChoice === DateChoicesEnum.DATETIMESLOT &&
        deliverySettings.deliveryTimeSlotType === KindOfTimeType.DIFFERENT && (
          <CopySetup selectedWorkingDay={selectedWorkingDay} selectTabDays={selectTabDays} />
        )}
      {deliverySettings.deliveryChoice === DateChoicesEnum.DATETIME && (
        <>
          <BlockStack gap="400">
            {selectedWorkingDay && (
              <BlockStack gap="400">
                <DeliveryExactTime time24h={time24h} selectedWorkingDay={selectedWorkingDay} selectTabDays={selectTabDays} />
                {deliveryState.isSaving && errorFields.errorFields?.rule_addTimeSlot_exact && (
                  <InlineError fieldID="" message={t('error.isValidTimeSlot')} />
                )}
              </BlockStack>
            )}
          </BlockStack>
        </>
      )}

      {deliverySettings.deliveryChoice === DateChoicesEnum.DATETIMESLOT && selectedWorkingDay && (
        <>
          <BlockStack gap="200">
            {!!selectedWorkingDay && selectedWorkingDay?.timeSlots && (
              <BlockStack gap="200">
                {selectedWorkingDay?.timeSlots?.map((timeSlot: ITimeSlot, index: number) => (
                  <TimeSlot
                    key={`time-slot-${selectedWorkingDay?.id}-${index}`}
                    index={index}
                    time24h={time24h}
                    timeSlot={timeSlot}
                    listTimeSlots={selectedWorkingDay?.timeSlots}
                    setTimeSlot={(slot) => handleChangeTimeSlot(slot, index)}
                    selectedDay={selectTabDays}
                    onDeleteTimeSlot={() => handleDeleteTimeSlot(index)}
                  />
                ))}
                {deliveryState.isSaving && errorFields.errorFields?.rule_addTimeSlot && deliveryState.isCheckingTimeSlot && (
                  <InlineError fieldID="" message={t('error.isValidTimeSlot')} />
                )}
                {deliveryState.isSaving &&
                  deliveryState.isCheckingTimeSlot &&
                  errorFields.errorFields?.rule_duplicateTimeSLot &&
                  !errorFields.errorFields?.rule_addTimeSlot && <InlineError fieldID="" message={t('error.duplicateTimeSlot')} />}
                {deliveryState.isSaving &&
                  deliveryState.isCheckingTimeSlot &&
                  errorFields.errorFields?.requireTimeSlot &&
                  !errorFields.errorFields?.rule_addTimeSlot && <InlineError fieldID="" message={t('error.requireTimeSlot')} />}
              </BlockStack>
            )}
            <Button icon={PlusCircleIcon} onClick={handleAddTimeSlot} fullWidth disabled={checkDisableAddTimeSlots}>
              {t('sections.deliveryAvailability.addTime')}
            </Button>
          </BlockStack>
        </>
      )}
    </BlockStack>
  );
};

export default DeliveryTime;
